import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Callout } from './Callout.js';
import { Skeleton } from './Skeleton.js';
import { ApiError } from '../../services/api/errors.js';

/**
 * Renders the three states a `useQuery` can be in, so a page cannot render only one of them.
 *
 * WHY THIS EXISTS
 *
 * 59 of 135 files calling `useQuery` had no `isError`/`error` branch (QG-CLI-PAGES-009). A failed
 * fetch therefore rendered as an empty list — visually identical to "you have no data yet". The
 * user is told the wrong thing, and there is no retry affordance because nothing knows a retry is
 * warranted. It is a whole-class defect rather than 59 separate oversights: `useQuery` returns a
 * bag of optional fields, so handling only `data` type-checks perfectly.
 *
 * The render-prop shape is the point. `children` receives `data` **non-nullable**, which means:
 *   - the error and loading branches cannot be skipped — there is no way to reach `data` without
 *     going through this component;
 *   - `data?.items ?? []` defensive noise disappears from call sites, and with it the habit of
 *     treating "no data" and "request failed" as the same thing.
 *
 * Deliberately NOT an error boundary: React error boundaries catch render-time throws, whereas a
 * rejected query is ordinary state. Both exist; this handles the second.
 *
 * Usage:
 *   <QueryBoundary query={workflowsQuery} label="workflows">
 *     {(workflows) => <WorkflowTable rows={workflows} />}
 *   </QueryBoundary>
 */

export interface QueryBoundaryProps<TData> {
  query: Pick<UseQueryResult<TData, unknown>, 'data' | 'isPending' | 'isError' | 'error' | 'refetch'>;
  /**
   * What is being loaded, lowercase and plural where natural ("workflows", "the daemon"). Used in
   * the failure message, so it should read inside "Could not load {label}."
   */
  label: string;
  children: (data: TData) => React.ReactNode;
  /** Loading placeholder. Defaults to three skeleton rows; pass a table//card-shaped one to match. */
  loading?: React.ReactNode;
  /**
   * Render the error inline (no card chrome) — for a boundary sitting inside a panel that already
   * has its own frame.
   */
  compact?: boolean;
}

/** The most useful sentence we can produce from whatever the query rejected with. */
export function describeQueryError(error: unknown, label: string): string {
  if (error instanceof ApiError) {
    // 401/403 are not "something went wrong" — they are an answer, and a retry will not change it.
    if (error.status === 401) return 'Your session has expired. Sign in again to continue.';
    if (error.status === 403) return `You do not have permission to view ${label}.`;
    if (error.status === 404) return `${label.charAt(0).toUpperCase()}${label.slice(1)} could not be found.`;
    return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return `Could not load ${label}.`;
}

/** True when retrying could plausibly succeed — a permission denial is not worth a retry button. */
function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status !== 401 && error.status !== 403 && error.status !== 404;
  return true;
}

export function QueryBoundary<TData>({
  query,
  label,
  children,
  loading,
  compact = false,
}: QueryBoundaryProps<TData>): React.ReactElement {
  if (query.isError) {
    const message = describeQueryError(query.error, label);
    const retry = isRetryable(query.error) ? (
      <button
        type="button"
        onClick={() => {
          void query.refetch();
        }}
        className="text-xs font-medium underline underline-offset-2"
      >
        Try again
      </button>
    ) : undefined;

    if (compact) {
      return (
        <div className="flex items-center gap-2 py-3 text-sm text-danger">
          <AlertTriangle size={14} className="shrink-0" />
          <span>{message}</span>
          {retry}
        </div>
      );
    }

    return (
      <Callout tone="danger" icon={AlertTriangle} title={`Could not load ${label}`} actions={retry}>
        {message}
      </Callout>
    );
  }

  // `isPending` (not `isLoading`) so a query disabled via `enabled: false` also shows the
  // placeholder rather than falling through to `children` with undefined data.
  if (query.isPending || query.data === undefined) {
    return <>{loading ?? <DefaultQueryLoading />}</>;
  }

  return <>{children(query.data)}</>;
}

function DefaultQueryLoading(): React.ReactElement {
  return (
    <div className="space-y-2 py-2" aria-busy="true" aria-live="polite">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}
